import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import {
  FiCreditCard, FiPlus, FiSearch, FiFilter, FiRefreshCw, FiEye, FiDownload,
  FiDollarSign, FiTrendingUp, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle,
  FiX, FiCopy, FiCheck, FiExternalLink, FiShield, FiCamera, FiHash, FiUser
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer, AdminHeader, HeaderContent, HeaderText, HeaderActions,
  ActionButton, MainContent, StatsGrid, StatCard, StatHeader, StatIcon,
  StatValue, StatLabel, SearchAndFilters, SearchBox, FilterSelect,
  ActionButtonGroup, DataTable, TableActionButton, Modal, ModalContent,
  ModalActions, ResultsCounter, LoadingSpinner, ErrorMessage
} from '../../styles/AdminComponents';

// Modal form specific styled components
import styled from 'styled-components';

// Enhanced Mobile-responsive payment card
const PaymentCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  
  .payment-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    gap: 1rem;
    
    .payment-info {
      flex: 1;
      min-width: 0;
      
      .payment-id {
        font-size: 1.1rem;
        font-weight: 600;
        color: #111827;
        margin-bottom: 0.25rem;
        word-break: break-all;
        font-family: 'Monaco', 'Consolas', monospace;
      }
      
      .license-key {
        font-size: 0.8rem;
        font-family: 'Monaco', 'Menlo', monospace;
        background: #f3f4f6;
        padding: 0.25rem 0.5rem;
        border-radius: 0.25rem;
        color: #6b7280;
        word-break: break-all;
      }
    }
    
    .actions {
      display: flex;
      gap: 0.5rem;
      flex-shrink: 0;
    }
  }
  
  .payment-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: 0.75rem;
    }
    
    .detail-item {
      .label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .value {
        font-size: 0.9rem;
        color: #111827;
        font-weight: 500;
      }
      
      .badge {
        display: inline-block;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
      }
    }
  }
  
  .enhanced-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    
    .stat-item {
      text-align: center;
      
      .stat-label {
        font-size: 0.7rem;
        color: #6b7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.25rem;
      }
      
      .stat-value {
        font-size: 1rem;
        font-weight: 700;
        color: #111827;
      }
    }
  }
`;

// Responsive container that switches between table and cards
const ResponsiveContainer = styled.div`
  .desktop-view {
    display: block;
    
    @media (max-width: 768px) {
      display: none;
    }
  }
  
  .mobile-view {
    display: none;
    
    @media (max-width: 768px) {
      display: block;
    }
  }
`;

// Payment Details Modal (ensure it's properly styled and functional)
const PaymentDetailsModal = styled(Modal)`
  .modal-content {
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const PaymentDetailSection = styled.div`
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .detail-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 640px) {
      grid-template-columns: 1fr;
    }
    
    .detail-item {
      .label {
        font-size: 0.8rem;
        color: #6b7280;
        font-weight: 500;
        margin-bottom: 0.25rem;
      }
      
      .value {
        color: #111827;
        font-weight: 500;
        word-break: break-all;
        
        &.mono {
          font-family: 'Monaco', 'Consolas', monospace;
          background: #f3f4f6;
          padding: 0.5rem;
          border-radius: 4px;
          font-size: 0.85rem;
        }
        
        &.currency {
          color: #10b981;
          font-weight: 600;
        }
      }
      
      &.full-width {
        grid-column: 1 / -1;
      }
    }
  }
`;

const CopyButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  margin-left: 0.5rem;
  
  &:hover {
    background: #2563eb;
  }
  
  &.copied {
    background: #10b981;
  }
`;

const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [networkFilter, setNetworkFilter] = useState('all');
  const [paymentStats, setPaymentStats] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [copiedFields, setCopiedFields] = useState({});

  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    if (adminRank === 'owner') {
      fetchPayments();
    }
  }, [adminRank]);

  useEffect(() => {
    const filtered = payments.filter(payment => {
      const matchesSearch = !searchTerm || 
        payment.payment_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (payment.license_key && payment.license_key.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (payment.order_id && payment.order_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (payment.payin_hash && payment.payin_hash.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;

      const matchesPlan = planFilter === 'all' || 
        (payment.plan_id && payment.plan_id.toLowerCase().includes(planFilter.toLowerCase()));

      const matchesNetwork = networkFilter === 'all' || 
        (payment.network && payment.network === networkFilter);

      return matchesSearch && matchesStatus && matchesPlan && matchesNetwork;
    });

    setFilteredPayments(filtered);
  }, [payments, searchTerm, statusFilter, planFilter, networkFilter]);

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const res = await axios.get('/api/admin/payments', { headers });
      setPayments(res.data.payments || []);
      
      // Calculate comprehensive payment stats
      const paymentsData = res.data.payments || [];
      const stats = {
        total_payments: paymentsData.length,
        total_revenue: paymentsData
          .filter(p => ['confirmed', 'finished', 'early_confirmed'].includes(p.status))
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
        confirmed_payments: paymentsData.filter(p => ['confirmed', 'finished', 'early_confirmed'].includes(p.status)).length,
        pending_payments: paymentsData.filter(p => ['waiting', 'confirming', 'pending'].includes(p.status)).length,
        expired_payments: paymentsData.filter(p => ['expired', 'failed'].includes(p.status)).length,
        camera_payments: paymentsData.filter(p => p.camera_product === true).length,
        crypto_payments: paymentsData.filter(p => p.crypto_amount && p.crypto_currency).length,
        unique_networks: [...new Set(paymentsData.map(p => p.network).filter(Boolean))].length,
        avg_payment_amount: paymentsData.length > 0 
          ? paymentsData.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) / paymentsData.length 
          : 0
      };
      setPaymentStats(stats);
      
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch payments');
      setLoading(false);
      toast.error('Failed to fetch payments');
    }
  };

  const refreshPayments = () => {
    fetchPayments();
  };

  const exportPayments = () => {
    const csvContent = [
      ['Payment ID', 'License Key', 'Plan', 'Status', 'Amount', 'Currency', 'Network', 'Created', 'Expires', 'Transaction Hash'],
      ...filteredPayments.map(payment => [
        payment.payment_id,
        payment.license_key || 'N/A',
        payment.plan_id || 'N/A',
        payment.status,
        payment.amount,
        payment.currency,
        payment.network || 'N/A',
        formatDate(payment.created_at),
        formatDate(payment.expires_at),
        payment.payin_hash || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'payments_export.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleViewPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentDetails(true);
  };

  const closePaymentDetails = () => {
    setSelectedPayment(null);
    setShowPaymentDetails(false);
  };

  const copyToClipboard = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedFields({ ...copiedFields, [field]: true });
      setTimeout(() => {
        setCopiedFields({ ...copiedFields, [field]: false });
      }, 2000);
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
      case 'finished':
      case 'early_confirmed':
        return { background: '#dcfce7', color: '#166534', text: 'Confirmed' };
      case 'waiting':
      case 'confirming':
      case 'pending':
        return { background: '#fef3c7', color: '#92400e', text: 'Pending' };
      case 'expired':
      case 'failed':
        return { background: '#fee2e2', color: '#dc2626', text: 'Failed' };
      default:
        return { background: '#f3f4f6', color: '#6b7280', text: status || 'Unknown' };
    }
  };

  const getPlanBadge = (planId) => {
    if (!planId) return { background: '#f3f4f6', color: '#6b7280', text: 'No Plan' };
    
    if (planId.includes('lifetime')) return { background: '#dcfce7', color: '#166534', text: planId };
    if (planId.includes('trial')) return { background: '#fef3c7', color: '#92400e', text: planId };
    return { background: '#dbeafe', color: '#1d4ed8', text: planId };
  };

  const getNetworkBadge = (network) => {
    if (!network) return null;
    
    return {
      background: '#ede9fe',
      color: '#7c3aed',
      text: network.toUpperCase()
    };
  };

  const truncateHash = (hash, length = 8) => {
    if (!hash) return 'N/A';
    return hash.length > length ? `${hash.substring(0, length)}...` : hash;
  };

  if (adminRank !== 'owner') {
    return (
      <AdminLayout>
        <AdminContainer>
          <AdminHeader>
            <HeaderContent>
              <HeaderText>
                <h1>Access Denied</h1>
                <p>Only owners can view payments</p>
              </HeaderText>
            </HeaderContent>
          </AdminHeader>
        </AdminContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Payment Management</h1>
              <p>Monitor and manage all payment transactions</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={refreshPayments} variant="secondary">
                <FiRefreshCw />
                <span className="btn-text">Refresh</span>
              </ActionButton>
              <ActionButton onClick={exportPayments} variant="secondary">
                <FiDownload />
                <span className="btn-text">Export CSV</span>
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          {/* Enhanced Payment Stats Section */}
          {paymentStats && (
            <StatsGrid>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCreditCard color="#3b82f6" /></StatIcon>
                  <h3>Total Payments</h3>
                </StatHeader>
                <StatValue>{paymentStats.total_payments}</StatValue>
                <StatLabel>All Transactions</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiDollarSign color="#10b981" /></StatIcon>
                  <h3>Total Revenue</h3>
                </StatHeader>
                <StatValue>{formatCurrency(paymentStats.total_revenue)}</StatValue>
                <StatLabel>Confirmed Only</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCheckCircle color="#10b981" /></StatIcon>
                  <h3>Confirmed</h3>
                </StatHeader>
                <StatValue>{paymentStats.confirmed_payments}</StatValue>
                <StatLabel>Successful Payments</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiClock color="#f59e0b" /></StatIcon>
                  <h3>Pending</h3>
                </StatHeader>
                <StatValue>{paymentStats.pending_payments}</StatValue>
                <StatLabel>Awaiting Confirmation</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiCamera color="#8b5cf6" /></StatIcon>
                  <h3>Camera Product</h3>
                </StatHeader>
                <StatValue>{paymentStats.camera_payments}</StatValue>
                <StatLabel>Camera Licenses</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiHash color="#14b8a6" /></StatIcon>
                  <h3>Crypto Payments</h3>
                </StatHeader>
                <StatValue>{paymentStats.crypto_payments}</StatValue>
                <StatLabel>Blockchain Transactions</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiTrendingUp color="#06b6d4" /></StatIcon>
                  <h3>Networks</h3>
                </StatHeader>
                <StatValue>{paymentStats.unique_networks}</StatValue>
                <StatLabel>Payment Networks</StatLabel>
              </StatCard>
              <StatCard>
                <StatHeader>
                  <StatIcon><FiDollarSign color="#f59e0b" /></StatIcon>
                  <h3>Average Amount</h3>
                </StatHeader>
                <StatValue>{formatCurrency(paymentStats.avg_payment_amount)}</StatValue>
                <StatLabel>Per Transaction</StatLabel>
              </StatCard>
            </StatsGrid>
          )}

          <SearchAndFilters>
            <SearchBox>
              <FiSearch />
              <input
                type="text"
                placeholder="Search payments, license keys, transaction hashes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBox>
            <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="finished">Finished</option>
              <option value="early_confirmed">Early Confirmed</option>
              <option value="waiting">Waiting</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="failed">Failed</option>
            </FilterSelect>
            <FilterSelect value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
              <option value="all">All Plans</option>
              <option value="trial">Trial</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="lifetime">Lifetime</option>
            </FilterSelect>
            <FilterSelect value={networkFilter} onChange={(e) => setNetworkFilter(e.target.value)}>
              <option value="all">All Networks</option>
              <option value="btc">Bitcoin</option>
              <option value="eth">Ethereum</option>
              <option value="ltc">Litecoin</option>
              <option value="trx">Tron</option>
              <option value="bnb">Binance</option>
            </FilterSelect>
          </SearchAndFilters>

          <ActionButtonGroup>
            <ResultsCounter>{filteredPayments.length} payments found</ResultsCounter>
          </ActionButtonGroup>

          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : (
            <ResponsiveContainer>
              {/* Desktop Table View */}
              <div className="desktop-view">
                <DataTable>
                  <thead>
                    <tr>
                      <th>Payment ID</th>
                      <th>License Key</th>
                      <th>Plan</th>
                      <th>Status</th>
                      <th>Amount</th>
                      <th>Network</th>
                      <th>Transaction</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((payment, index) => {
                      const statusBadge = getStatusBadge(payment.status);
                      const planBadge = getPlanBadge(payment.plan_id);
                      const networkBadge = getNetworkBadge(payment.network);
                      
                      return (
                        <tr key={payment.payment_id || index}>
                          <td>
                            <code className="payment-code-cell">{payment.payment_id}</code>
                          </td>
                          <td>
                            <code className="code-cell">{payment.license_key || 'N/A'}</code>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: '500',
                              background: planBadge.background,
                              color: planBadge.color
                            }}>
                              {planBadge.text}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.8em',
                              fontWeight: '500',
                              background: statusBadge.background,
                              color: statusBadge.color
                            }}>
                              {statusBadge.text}
                            </span>
                          </td>
                          <td>
                            <div className="amount-cell">
                              <span className="amount">{formatCurrency(payment.amount, payment.currency)}</span>
                              {payment.crypto_amount && (
                                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                  {payment.crypto_amount} {payment.crypto_currency}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {networkBadge && (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '0.75em',
                                background: networkBadge.background,
                                color: networkBadge.color
                              }}>
                                {networkBadge.text}
                              </span>
                            ) || 'N/A'}
                          </td>
                          <td>
                            {payment.payin_hash ? (
                              <code className="code-cell" title={payment.payin_hash}>
                                {truncateHash(payment.payin_hash)}
                              </code>
                            ) : 'N/A'}
                          </td>
                          <td>{formatDate(payment.created_at)}</td>
                          <td>{formatDate(payment.expires_at)}</td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <TableActionButton 
                                onClick={() => handleViewPayment(payment)}
                                title="View Details"
                              >
                                <FiEye />
                              </TableActionButton>
                              {payment.payment_url && (
                                <TableActionButton 
                                  onClick={() => window.open(payment.payment_url, '_blank')}
                                  title="Open Payment Page"
                                >
                                  <FiExternalLink />
                                </TableActionButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </DataTable>
              </div>

              {/* Enhanced Mobile Card View */}
              <div className="mobile-view">
                {filteredPayments.map((payment, index) => {
                  const statusBadge = getStatusBadge(payment.status);
                  const planBadge = getPlanBadge(payment.plan_id);
                  const networkBadge = getNetworkBadge(payment.network);
                  
                  return (
                    <PaymentCard key={payment.payment_id || index}>
                      <div className="payment-header">
                        <div className="payment-info">
                          <div className="payment-id">{payment.payment_id}</div>
                          <div className="license-key">{payment.license_key || 'No License Key'}</div>
                        </div>
                        <div className="actions">
                          <TableActionButton 
                            onClick={() => handleViewPayment(payment)}
                            title="View Details"
                          >
                            <FiEye />
                          </TableActionButton>
                          {payment.payment_url && (
                            <TableActionButton 
                              onClick={() => window.open(payment.payment_url, '_blank')}
                              title="Open Payment Page"
                            >
                              <FiExternalLink />
                            </TableActionButton>
                          )}
                        </div>
                      </div>
                      
                      <div className="payment-details">
                        <div className="detail-item">
                          <div className="label">Plan</div>
                          <span className="badge" style={{
                            background: planBadge.background,
                            color: planBadge.color
                          }}>
                            {planBadge.text}
                          </span>
                        </div>
                        <div className="detail-item">
                          <div className="label">Status</div>
                          <span className="badge" style={{
                            background: statusBadge.background,
                            color: statusBadge.color
                          }}>
                            {statusBadge.text}
                          </span>
                        </div>
                        <div className="detail-item">
                          <div className="label">Amount</div>
                          <div className="value">{formatCurrency(payment.amount, payment.currency)}</div>
                        </div>
                        <div className="detail-item">
                          <div className="label">Created</div>
                          <div className="value">{formatDate(payment.created_at)}</div>
                        </div>
                      </div>
                      
                      <div className="enhanced-details">
                        <div className="stat-item">
                          <div className="stat-label">Network</div>
                          <div className="stat-value">
                            {networkBadge ? (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '0.7em',
                                background: networkBadge.background,
                                color: networkBadge.color
                              }}>
                                {networkBadge.text}
                              </span>
                            ) : 'N/A'}
                          </div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Transaction</div>
                          <div className="stat-value">{truncateHash(payment.payin_hash)}</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Expires</div>
                          <div className="stat-value">{formatDate(payment.expires_at)}</div>
                        </div>
                        {payment.camera_product && (
                          <div className="stat-item">
                            <div className="stat-label">Product</div>
                            <div className="stat-value">
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontSize: '0.7em',
                                background: '#ede9fe',
                                color: '#7c3aed'
                              }}>
                                CAMERA
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </PaymentCard>
                  );
                })}
              </div>
            </ResponsiveContainer>
          )}

          {/* Enhanced Payment Details Modal */}
          {showPaymentDetails && selectedPayment && (
            <PaymentDetailsModal>
              <ModalContent className="modal-content">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                  <h2>💳 Payment Details: {selectedPayment.payment_id}</h2>
                  <button 
                    onClick={closePaymentDetails}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      fontSize: '1.5rem', 
                      cursor: 'pointer',
                      color: '#6b7280',
                      padding: '0.5rem',
                      borderRadius: '0.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FiX />
                  </button>
                </div>

                <PaymentDetailSection>
                  <h3><FiCreditCard /> Basic Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Payment ID</div>
                      <div className="value mono">
                        {selectedPayment.payment_id}
                        <CopyButton 
                          className={copiedFields.payment_id ? 'copied' : ''}
                          onClick={() => copyToClipboard(selectedPayment.payment_id, 'payment_id')}
                        >
                          {copiedFields.payment_id ? <FiCheck /> : <FiCopy />}
                        </CopyButton>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Order ID</div>
                      <div className="value mono">{selectedPayment.order_id || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Plan</div>
                      <div className="value">
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.9em',
                          fontWeight: '500',
                          ...getPlanBadge(selectedPayment.plan_id)
                        }}>
                          {getPlanBadge(selectedPayment.plan_id).text}
                        </span>
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Status</div>
                      <div className="value">
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.9em',
                          fontWeight: '500',
                          ...getStatusBadge(selectedPayment.status)
                        }}>
                          {getStatusBadge(selectedPayment.status).text}
                        </span>
                      </div>
                    </div>
                  </div>
                </PaymentDetailSection>

                <PaymentDetailSection>
                  <h3><FiDollarSign /> Payment Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Amount</div>
                      <div className="value currency">
                        {formatCurrency(selectedPayment.amount, selectedPayment.currency)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Currency</div>
                      <div className="value">{selectedPayment.currency?.toUpperCase() || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Crypto Amount</div>
                      <div className="value">
                        {selectedPayment.crypto_amount ? 
                          `${selectedPayment.crypto_amount} ${selectedPayment.crypto_currency}` : 'N/A'
                        }
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Actually Paid</div>
                      <div className="value currency">
                        {selectedPayment.actually_paid ? 
                          formatCurrency(selectedPayment.actually_paid, selectedPayment.currency) : 'N/A'
                        }
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Price (GBP)</div>
                      <div className="value">{selectedPayment.price_gbp ? `£${selectedPayment.price_gbp}` : 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Network</div>
                      <div className="value">
                        {selectedPayment.network ? (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.9em',
                            fontWeight: '500',
                            ...getNetworkBadge(selectedPayment.network)
                          }}>
                            {getNetworkBadge(selectedPayment.network).text}
                          </span>
                        ) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </PaymentDetailSection>

                <PaymentDetailSection>
                  <h3><FiHash /> Transaction Details</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <div className="label">Pay Address</div>
                      <div className="value mono">{selectedPayment.pay_address || 'N/A'}</div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="label">Pay-in Hash</div>
                      <div className="value mono">
                        {selectedPayment.payin_hash || 'N/A'}
                        {selectedPayment.payin_hash && (
                          <CopyButton 
                            className={copiedFields.payin_hash ? 'copied' : ''}
                            onClick={() => copyToClipboard(selectedPayment.payin_hash, 'payin_hash')}
                          >
                            {copiedFields.payin_hash ? <FiCheck /> : <FiCopy />}
                          </CopyButton>
                        )}
                      </div>
                    </div>
                    <div className="detail-item full-width">
                      <div className="label">Payout Hash</div>
                      <div className="value mono">
                        {selectedPayment.payout_hash || 'N/A'}
                        {selectedPayment.payout_hash && (
                          <CopyButton 
                            className={copiedFields.payout_hash ? 'copied' : ''}
                            onClick={() => copyToClipboard(selectedPayment.payout_hash, 'payout_hash')}
                          >
                            {copiedFields.payout_hash ? <FiCheck /> : <FiCopy />}
                          </CopyButton>
                        )}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Purchase ID</div>
                      <div className="value mono">{selectedPayment.purchase_id || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Payment Type</div>
                      <div className="value">{selectedPayment.payment_type || 'N/A'}</div>
                    </div>
                  </div>
                </PaymentDetailSection>

                <PaymentDetailSection>
                  <h3><FiUser /> License Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item full-width">
                      <div className="label">License Key</div>
                      <div className="value mono">
                        {selectedPayment.license_key || 'N/A'}
                        {selectedPayment.license_key && (
                          <CopyButton 
                            className={copiedFields.license_key ? 'copied' : ''}
                            onClick={() => copyToClipboard(selectedPayment.license_key, 'license_key')}
                          >
                            {copiedFields.license_key ? <FiCheck /> : <FiCopy />}
                          </CopyButton>
                        )}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Camera Product</div>
                      <div className="value">
                        {selectedPayment.camera_product ? (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.9em',
                            fontWeight: '500',
                            background: '#ede9fe',
                            color: '#7c3aed'
                          }}>
                            <FiCamera style={{ marginRight: '4px' }} />
                            Camera License
                          </span>
                        ) : 'Standard License'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Camera User ID</div>
                      <div className="value">{selectedPayment.camera_user_id || 'N/A'}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Credentials Viewed</div>
                      <div className="value">
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '0.9em',
                          fontWeight: '500',
                          background: selectedPayment.credentials_viewed ? '#dcfce7' : '#fee2e2',
                          color: selectedPayment.credentials_viewed ? '#166534' : '#dc2626'
                        }}>
                          {selectedPayment.credentials_viewed ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </PaymentDetailSection>

                <PaymentDetailSection>
                  <h3><FiClock /> Timeline</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <div className="label">Created</div>
                      <div className="value">{formatDateTime(selectedPayment.created_at)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Updated</div>
                      <div className="value">{formatDateTime(selectedPayment.updated_at)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Expires</div>
                      <div className="value">{formatDateTime(selectedPayment.expires_at)}</div>
                    </div>
                    <div className="detail-item">
                      <div className="label">Referrer</div>
                      <div className="value">
                        {selectedPayment.referrer ? (
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '0.9em',
                            fontWeight: '500',
                            background: '#dbeafe',
                            color: '#1d4ed8'
                          }}>
                            {selectedPayment.referrer}
                          </span>
                        ) : 'Direct'}
                      </div>
                    </div>
                  </div>
                </PaymentDetailSection>

                <ModalActions>
                  {selectedPayment.payment_url && (
                    <ActionButton 
                      onClick={() => window.open(selectedPayment.payment_url, '_blank')}
                      variant="secondary"
                    >
                      <FiExternalLink />
                      Open Payment Page
                    </ActionButton>
                  )}
                  <ActionButton onClick={closePaymentDetails}>
                    Close
                  </ActionButton>
                </ModalActions>
              </ModalContent>
            </PaymentDetailsModal>
          )}
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default PaymentsPage; 